import { Text, View } from "@react-pdf/renderer";
import { useMemo } from "react";
import { chunk } from "../../../../../utils/chunk";
import { PDFTable, tableStyles } from "./PDFTable";

interface PDFPalletsSummaryProps {
    fields: any[];
    printChildren: any[];
    config: any;
}

export function PDFPalletsSummary({ fields, printChildren, config }: PDFPalletsSummaryProps) {
    const columnWeights = useMemo(
        () => fields.map((field) => field.field_config?.pdf_column_width_weight),
        [fields]
    );

    // stack the columns, starting with the headers, followed by content and finally the footer
    const headers = useMemo(() => fields.map((i, index) => (
        <View key={index} style={[tableStyles.tableCell, tableStyles.tableCellHeight, tableStyles.tableHeader]} >
            <Text style={tableStyles.tableHeaderText}>
                {i.label || "\u00A0" }
            </Text>
        </View>
    )), [fields]);

    const allColumns = useMemo(() => fields.map((i, index) => [
        ...(printChildren.map((p) => {
            const filteredField = config.applyDisplayFilterLayerFields([i], {
                layer_type: p.type,
                fruit_type: p.fruit_type,
                layer: p,
                check: p.latest_check,
            });

            if (filteredField.length === 0) {
                return (
                    <View style={[tableStyles.tableCellHeight, tableStyles.tableCell]} key={`${index + 1}-${p.id}`}>
                        <Text>{"\u00A0"}</Text>
                    </View>
                );
            }

            return (
                <View style={[tableStyles.tableCellHeight, tableStyles.tableCell]} key={`${index + 1}-${p.id}`}>
                    {filteredField[0]
                        .clone()
                        .set_value({
                            config,
                            layer: p,
                            check: p.latest_check, // * To be used in check tables and layer overview
                            fruit: null, // * Fruit list is not available on overview screen
                            children: null, // * children is only available for layer index your are viewing
                            form_options: config.form_options // * used to translate form values to labels

                        })
                        .display_vector(7)}
                </View>
            );
        }))
    ]), [fields, printChildren, config]);

    // make chunks of all columns with 25 rows per column
    const chunked = useMemo(() => fields.map((_, index) => chunk(allColumns[index], 25)), [allColumns, fields]);

    return chunked[0].map((_, chunkIndex) => (
        <PDFTable
            key={chunkIndex}
            columns={chunked.map((i, colIndex) => [headers[colIndex], ...i[chunkIndex]])}
            columnWeights={columnWeights}
        />
    ));
}
